import { useEffect, useRef, useState } from 'react';
import { ChevronDown, CheckCircle2, Circle, Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  ConfirmModal,
  ProviderLogo,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { deriveLogoSlug } from '@/pages/credentials/identity-lib';
import {
  useOrgProviderConfig,
  useOrgProviderConfigs,
  useRemoveProviderConfig,
  useSaveProviderConfig,
} from '@/hooks/useOrgProviderConfigs';
import type {
  ProviderConfigField,
  ProviderConfigIndexEntry,
} from '@/types/identity';
import {
  buildSaveValues,
  parseList,
  seedFormValues,
  validateConfig,
  visibleFields,
} from './providerConfig.helpers';

/**
 * Slug for the bundled `/logos/<slug>-icon.svg` set. AWS IAM Identity Center
 * ships as `aws-icon.svg`, but its id (`aws-sso`) and displayName slug don't
 * match — special-case it so the real mark shows instead of a monogram.
 * Everything else uses the generic best-effort slug.
 */
function providerLogoSlug(id: string, displayName: string): string {
  if (id === 'aws-sso' || /^aws\b/i.test(displayName)) return 'aws';
  return deriveLogoSlug(id, displayName);
}

/**
 * Org-admin "Providers" tab — schema-driven configuration for identity
 * providers (AWS SSO start URL/region, OAuth client id/secret for x/google/…).
 * Every form is rendered from the provider's `fields` schema, so adding a
 * provider to the backend catalog makes its form appear with no UI changes.
 *
 * Layout is a vertical accordion: one row per configurable provider with a
 * Configured/Set-up badge; expanding a row mounts its form (which lazily loads
 * the provider's current masked values). The whole surface is gated to org
 * admins by <RequireOrgAdmin> on the parent route.
 */
export default function OrgProvidersConfig() {
  const indexQuery = useOrgProviderConfigs();
  const [expanded, setExpanded] = useState<string | null>(null);

  const providers = indexQuery.data?.providers ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Configure what each identity provider needs — an IAM Identity Center
        portal URL, or your organization&rsquo;s own OAuth app credentials.
        These apply org-wide.
      </p>

      {indexQuery.isPending && (
        <div className="rounded-xl border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
          Loading providers…
        </div>
      )}

      {indexQuery.isError && (
        <div className="rounded-xl border border-red-300 bg-card px-6 py-10 text-center text-sm text-red-600">
          {indexQuery.error instanceof Error
            ? indexQuery.error.message
            : 'Failed to load providers.'}
        </div>
      )}

      {!indexQuery.isPending && !indexQuery.isError && providers.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
          No configurable providers are available for your organization yet.
        </div>
      )}

      <div className="space-y-3">
        {providers.map((entry) => (
          <ProviderRow
            key={entry.id}
            entry={entry}
            isExpanded={expanded === entry.id}
            onToggle={() =>
              setExpanded((cur) => (cur === entry.id ? null : entry.id))
            }
          />
        ))}
      </div>
    </div>
  );
}

function ProviderRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: ProviderConfigIndexEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden transition-colors hover:border-primary-500/40">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left focus-ring"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ProviderLogo
            slug={providerLogoSlug(entry.id, entry.displayName)}
            displayName={entry.displayName}
            className="h-7 w-7 shrink-0 mr-3"
            grayscale
          />
          <span className="text-base font-semibold text-foreground truncate">
            {entry.displayName}
          </span>
          <ConfiguredBadge configured={entry.configured} />
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            isExpanded && 'rotate-180',
          )}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-border px-5 py-5">
          <ProviderConfigForm providerId={entry.id} displayName={entry.displayName} />
        </div>
      )}
    </div>
  );
}

function ConfiguredBadge({ configured }: { configured: boolean }) {
  return configured ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-3 w-3" />
      Configured
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <Circle className="h-3 w-3" />
      Set up
    </span>
  );
}

/**
 * The schema-driven form for a single provider. Loads the provider's current
 * masked config, seeds the inputs once, and saves honouring the merge rules
 * (non-secret replace, secret merge — see providerConfig.helpers).
 */
function ProviderConfigForm({
  providerId,
  displayName,
}: {
  providerId: string;
  displayName: string;
}) {
  const { success, error: showError } = useToast();
  const detailQuery = useOrgProviderConfig(providerId, true);
  const saveMutation = useSaveProviderConfig();
  const removeMutation = useRemoveProviderConfig();

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [seeded, setSeeded] = useState(false);
  // Secret fields that the admin has chosen to replace (input shown instead of
  // the masked "Saved" state).
  const [replacing, setReplacing] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const detail = detailQuery.data;

  // Seed editable state once the masked config arrives.
  useEffect(() => {
    if (!seeded && detail) {
      setFormValues(seedFormValues(detail));
      setSeeded(true);
    }
  }, [seeded, detail]);

  if (detailQuery.isPending) {
    return <p className="text-sm text-muted-foreground">Loading configuration…</p>;
  }
  if (detailQuery.isError || !detail) {
    return (
      <p className="text-sm text-red-600">
        {detailQuery.error instanceof Error
          ? detailQuery.error.message
          : 'Failed to load configuration.'}
      </p>
    );
  }

  const fields = visibleFields(detail.fields);
  const secrets = detail.secrets ?? {};

  if (fields.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This provider has no configurable settings.
      </p>
    );
  }

  const setValue = (key: string, value: string) => {
    setFormValues((cur) => ({ ...cur, [key]: value }));
    if (errors[key]) setErrors((cur) => ({ ...cur, [key]: '' }));
  };

  const startReplace = (key: string) =>
    setReplacing((cur) => new Set(cur).add(key));

  const cancelReplace = (key: string) => {
    setReplacing((cur) => {
      const next = new Set(cur);
      next.delete(key);
      return next;
    });
    setValue(key, '');
  };

  const handleSave = async () => {
    setFormError(null);
    const found = validateConfig(detail.fields, formValues, secrets);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    try {
      const fresh = await saveMutation.mutateAsync({
        providerId,
        values: buildSaveValues(detail.fields, formValues),
      });
      // Re-seed from the returned masked shape: clears secret inputs and picks
      // up any server normalisation of non-secret values.
      setFormValues(seedFormValues(fresh));
      setReplacing(new Set());
      setErrors({});
      success(`${displayName} configuration saved`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save configuration.');
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync(providerId);
      setFormValues(seedFormValues({ fields: detail.fields, values: {} }));
      setReplacing(new Set());
      setErrors({});
      setFormError(null);
      setConfirmRemove(false);
      success(`${displayName} configuration removed`);
    } catch (e) {
      setConfirmRemove(false);
      showError(e instanceof Error ? e.message : 'Failed to remove configuration.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        {fields.map((field) => (
          <FieldControl
            key={field.key}
            field={field}
            value={formValues[field.key] ?? ''}
            error={errors[field.key]}
            secretConfigured={secrets[field.key]?.configured ?? false}
            isReplacing={replacing.has(field.key)}
            onChange={(v) => setValue(field.key, v)}
            onStartReplace={() => startReplace(field.key)}
            onCancelReplace={() => cancelReplace(field.key)}
          />
        ))}
      </div>

      {formError && (
        <p className="text-sm text-red-600" role="alert">
          {formError}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        {detail.configured ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setConfirmRemove(true)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove configuration
          </Button>
        ) : (
          <span />
        )}
        <Button onClick={handleSave} isLoading={saveMutation.isPending}>
          Save
        </Button>
      </div>

      <ConfirmModal
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={handleRemove}
        title={`Remove ${displayName} configuration?`}
        message="This clears all stored values and secrets for this provider. Connections relying on it may stop working."
        confirmText="Remove"
        isLoading={removeMutation.isPending}
      />
    </div>
  );
}

/** Renders one field according to its `type`. */
function FieldControl({
  field,
  value,
  error,
  secretConfigured,
  isReplacing,
  onChange,
  onStartReplace,
  onCancelReplace,
}: {
  field: ProviderConfigField;
  value: string;
  error?: string;
  secretConfigured: boolean;
  isReplacing: boolean;
  onChange: (value: string) => void;
  onStartReplace: () => void;
  onCancelReplace: () => void;
}) {
  const id = `provider-field-${field.key}`;

  if (field.type === 'select') {
    return (
      <Select
        id={id}
        label={field.label}
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
        placeholder={field.placeholder || 'Select…'}
        options={field.options ?? []}
        error={error}
        hint={field.helpText}
      />
    );
  }

  if (field.type === 'secret') {
    // Stored secret + not actively replacing → show the masked "Saved" state.
    if (secretConfigured && !isReplacing) {
      return (
        <div className="w-full">
          <Label htmlFor={id}>{field.label}</Label>
          <div className="flex items-center gap-3">
            <span className="flex h-10 flex-1 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
              •••••••• Saved
            </span>
            <Button type="button" variant="outline" size="sm" onClick={onStartReplace}>
              Replace
            </Button>
          </div>
          {field.helpText && (
            <p className="mt-1 text-sm text-muted-foreground">{field.helpText}</p>
          )}
        </div>
      );
    }

    return (
      <div className="w-full">
        <div className="flex items-center justify-between">
          <Label htmlFor={id}>{field.label}</Label>
          {secretConfigured && isReplacing && (
            <button
              type="button"
              onClick={onCancelReplace}
              className="text-xs text-muted-foreground hover:text-foreground focus-ring rounded"
            >
              Cancel
            </button>
          )}
        </div>
        <Input
          id={id}
          type="password"
          autoComplete="new-password"
          value={value}
          onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
          placeholder={field.placeholder}
          error={error}
          hint={field.helpText}
        />
      </div>
    );
  }

  if (field.type === 'string-list') {
    return (
      <StringListField
        field={field}
        value={value}
        error={error}
        id={id}
        onChange={onChange}
      />
    );
  }

  // string | url
  return (
    <div className="w-full">
      <Label htmlFor={id}>{field.label}</Label>
      <Input
        id={id}
        type={field.type === 'url' ? 'url' : 'text'}
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
        placeholder={field.placeholder}
        error={error}
        hint={field.helpText}
      />
    </div>
  );
}

/**
 * Repeatable add/remove rows for a `string-list` field. The field's value is a
 * single JSON-array string (the `values` map is `Record<string,string>`), so
 * this owns local `rows` state and serialises trimmed, non-empty rows back to
 * that string on every edit — always a valid array ('[]' when empty).
 *
 * Rows are re-synced when `value` changes externally (e.g. the form re-seeds
 * after a save), tracked via `lastEmitted` so our own edits don't reset the
 * row the admin is typing into.
 */
function StringListField({
  field,
  value,
  error,
  id,
  onChange,
}: {
  field: ProviderConfigField;
  value: string;
  error?: string;
  id: string;
  onChange: (value: string) => void;
}) {
  // State always carries at least one (possibly empty) row to type into.
  const [rows, setRows] = useState<string[]>(() => {
    const parsed = parseList(value);
    return parsed.length ? parsed : [''];
  });
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      const parsed = parseList(value);
      setRows(parsed.length ? parsed : ['']);
    }
  }, [value]);

  const commit = (next: string[]) => {
    const forState = next.length ? next : [''];
    setRows(forState);
    const json = JSON.stringify(forState.map((r) => r.trim()).filter(Boolean));
    lastEmitted.current = json;
    onChange(json);
  };

  return (
    <div className="w-full">
      <Label htmlFor={id}>{field.label}</Label>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              id={i === 0 ? id : undefined}
              type="text"
              value={row}
              onChange={(e: { target: { value: string } }) =>
                commit(rows.map((r, idx) => (idx === i ? e.target.value : r)))
              }
              placeholder={field.placeholder}
              className={cn('flex-1', error && 'border-red-300 focus-visible:ring-red-500')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => commit(rows.filter((_, idx) => idx !== i))}
              aria-label="Remove entry"
              className="shrink-0 text-muted-foreground hover:text-red-600"
              disabled={rows.length === 1 && !row.trim()}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => commit([...rows, ''])}
        className="mt-2 text-primary-600 hover:text-primary-700"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add entry
      </Button>
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {field.helpText && (
        <p className="mt-1 text-sm text-muted-foreground">{field.helpText}</p>
      )}
    </div>
  );
}
